import * as React from "react";
import { Column } from "primereact/column";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Button } from "reactstrap";
import { Link } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import useEntitiesList from "../../utils/listHookNew";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "primereact/datatable";
import { getDateTime, DropdownFilterElement, renderHeader, filterDateTime } from "../../components/TableUtils";
import Can from "../../auth/Can";
import roles from "../../auth/roles";
import classNames from 'classnames';
import { deleteOrder } from "../../api/orders";

export default function OrderList({ opt, activeTenant, isBackOffice }) {
    let dtable;
    const { domains } = useAuth0();
    const [result, loading, setLoading, globalFilter, setGlobalFilter, forceRefresh] = useEntitiesList("orders", "createdat desc", isBackOffice ? undefined : `tenant_id='${activeTenant._id}'`);

    const [openModal, setOpenModal] = React.useState(false);
    const toggle = () => setOpenModal(!openModal);

    const onDelete = async id => {
        const value = await deleteOrder(id);
        toggle();
        forceRefresh();
        return value;
    };

    const modalSection = () => (
        <Modal zIndex={2000} centered size="sm" isOpen={openModal ? true : false} backdrop={true}>
            <ModalHeader toggle={toggle}>
                Eliminazione Ordine
        </ModalHeader>
            <ModalBody>
                <p>Confermi di voler eliminare l'ordine con Rif. {openModal.order_ref}?</p>
            </ModalBody>
            <ModalFooter>
                <Button color="link" className="btn-link-dark" onClick={toggle}>Annulla</Button>{' '}
                <Button color="danger" className="ml-auto" onClick={() => onDelete(openModal._id)}>Elimina</Button>
            </ModalFooter>
        </Modal>
    );

    const getHeader = () => {
        return (
            <Can
                roles={[roles.DEALER, roles.ADMIN]}
                yes={() =>
                    isBackOffice ? null : (
                        <div className="action-container">
                            <Link
                                className="mr-1"
                                title="Aggiungi ordine"
                                className="btn btn-primary p-button-icon-only"
                                to={`add-order`}
                            >
                                <FontAwesomeIcon icon="truck" /> Nuovo Ordine
                            </Link>
                        </div>
                    )
                }
                no={() => null}
            />
        );
    };

    const onRefresh = () => {
        forceRefresh();
        setGlobalFilter("");
    }

    const exportTable = () => {
        dtable.exportCSV();
    }

    return (
        <PageLayout
            title={
                isBackOffice
                    ? "Gestione Ordini"
                    : `Elenco Ordini - Dealer: ${activeTenant.name}`
            }
        >
            <Row>
                <Col>
                    <DataTable
                        ref={el => {
                            dtable = el;
                        }}
                        loading={loading}
                        value={result}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        className="p-datatable-hoverable-rows p-datatable-customers"
                        responsive
                        paginator={true}
                        autoLayout={false}
                        first={0}
                        rows={20}
                        totalRecords={result.length}
                        paginatorPosition="both"
                        header={renderHeader(loading, result.length, undefined, getHeader(), { globalFilter, setGlobalFilter, onRefresh }, exportTable)}
                        globalFilter={globalFilter}
                        emptyMessage={() => (<div className="text-center">Nessun Ordine in tabella</div>)}
                        paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        currentPageReportTemplate="Visualizzati {first} di {last} Ordini su un totale di {totalRecords}"
                    >
                        <Column
                            field="order_ref"
                            style={{ wordWrap: "break-word" }}
                            header="Rif. ordine"
                            sortable
                            filter
                            filterMatchMode="contains"
                            body={(rowData, column) => {
                                return (
                                    <Link
                                        to={`orders/${rowData._id}`}
                                    >
                                        {rowData.order_ref}
                                    </Link>
                                );
                            }}
                        />
                        <Column
                            field="_id"
                            style={{ wordWrap: "break-word" }}
                            header="Num. ordine"
                            sortable
                            filter
                            filterMatchMode="contains"
                        />
                        {isBackOffice && (
                            <Column
                                field="tenant"
                                header="Dealer"
                                sortable
                                filter
                                filterMatchMode="contains"
                                style={{ wordWrap: "break-word" }}
                            />
                        )}
                        <Column
                            field="attributes.created_for"
                            header="Cliente"
                            sortable
                            filter
                            filterMatchMode="contains"
                            style={{ wordWrap: "break-word" }}
                        />
                        <Column
                            field="attributes.model"
                            header="Modello"
                            sortable
                            filter
                            body={el =>
                                domains.devicesType.find(
                                    item => item.value === el.attributes.model
                                ).label
                            }
                            filterElement={
                                <DropdownFilterElement
                                    field="attributes.model"
                                    options={domains.devicesType}
                                    onChange={e => dtable.filter(e.value, e.field)}
                                />
                            }
                            style={{ wordWrap: "break-word" }}
                        />
                        <Column
                            field="quantity"
                            header="Tot."
                            sortable
                            filter
                            style={{ maxWidth: "50px", wordWrap: "break-word" }}
                        />
                        <Column
                            field="createdat"
                            header="Creato il"
                            sortable
                            filter
                            body={getDateTime}
                            filterMatchMode="custom"
                            filterFunction={filterDateTime}
                            style={{ wordWrap: "break-word" }}
                        />
                        <Column
                            field="attributes.creator_email"
                            header="Creato da"
                            sortable
                            filter
                            filterMatchMode="contains"
                            style={{ wordWrap: "break-word" }}
                        />
                        {!isBackOffice && (
                            <Column
                                field="updatedby"
                                header="Modificato da"
                                sortable
                                filter
                                filterMatchMode="contains"
                                style={{ wordWrap: "break-word" }}
                            />
                        )}
                        <Column
                            field="status"
                            header="Stato"
                            sortable
                            filter
                            filterMatchMode="equals"
                            filterElement={
                                <DropdownFilterElement
                                    field="status"
                                    options={domains.ordersType}
                                    onChange={e => { dtable.filter(domains.ordersType[e.value] != undefined ? domains.ordersType[e.value].label : "", e.field) }}
                                />
                            }
                            style={{ wordWrap: "break-word", textAlign: "center" }}
                            body={(rowData, column) => {
                                return (
                                    <span className={classNames('customer-badge', 'status-' + rowData.status)}>{rowData.status}</span>
                                );
                            }}
                        />
                        <Column
                            field="actions"
                            body={(rowData, column) => {
                                return (
                                    <div className="action-container">
                                        <Can
                                            roles={[roles.ADMIN, roles.AGENTE, roles.DEALER, roles.BACKOFFICE]}
                                            yes={() => (
                                                <Link
                                                    title="Gestisci ordine"
                                                    className="btn btn-sm btn-primary p-button-icon-only"
                                                    to={`orders/${rowData._id}`}
                                                >
                                                    <FontAwesomeIcon icon="pencil-alt" className="mr-1" />
                                                </Link>
                                            )}
                                            no={() => null}
                                        />
                                        {
                                            <Can
                                                roles={[roles.ADMIN, roles.DEALER]}
                                                yes={() =>
                                                    (["lavorazione"].includes(
                                                        rowData.status
                                                    ) && (
                                                            <Link
                                                                className="mr-1"
                                                                title="Prepara l'ordine"
                                                                className={`btn btn-sm p-button-icon-only btn-first`}
                                                                to={`/backoffice/create-order-delivery/${rowData._id}`}
                                                            >
                                                                <FontAwesomeIcon icon="sim-card" />
                                                            </Link>
                                                        )) || (
                                                        ["spedito", "consegnato", "preparato"].includes(
                                                            rowData.status
                                                        ) && (
                                                            <Link
                                                                className="mr-1"
                                                                title="Crea la spedizione"
                                                                className={`btn btn-sm p-button-icon-only btn-${rowData.status === "preparato" ? "warning" : rowData.status === "spedito" ? "info" : "success"}`}
                                                                to={`/backoffice/create-order-delivery/${rowData._id}`}
                                                            >
                                                                <FontAwesomeIcon icon="truck" />
                                                            </Link>
                                                        )
                                                    )}
                                                no={() =>
                                                    ["spedito", "consegnato"].includes(
                                                        rowData.status
                                                    ) && (
                                                        <Link
                                                            className="mr-1"
                                                            title="Controlla la spedizione"
                                                            className={`btn btn-sm p-button-icon-only btn-${rowData.status === "lavorazione" || rowData.status === "preparato" ? "warning" : "success"}`}
                                                            to={`orders/view-delivery/${rowData._id}`}
                                                        >
                                                            <FontAwesomeIcon icon="truck" />
                                                        </Link>
                                                    )
                                                }
                                            />
                                        }
                                        <Can
                                            roles={[roles.ADMIN]}
                                            yes={() => (
                                                <Button
                                                    color="danger"
                                                    title="Elimina Ordine"
                                                    onClick={() => setOpenModal(rowData)}
                                                    className="btn-sm p-button-icon-only"
                                                >
                                                    <FontAwesomeIcon icon="trash-alt" />
                                                </Button>
                                            )}
                                            no={() => null}
                                        />
                                    </div>
                                );
                            }}
                            style={{ wordWrap: "break-word" }}
                        />
                    </DataTable>
                </Col>
            </Row>
            {modalSection()}
        </PageLayout>
    );
}
